import { generatePath, useSearchParams } from 'react-router-dom';
import { appPaths, appRoutes } from 'consts/paths/paths';
import { useCurrentChild } from 'jotaiAtoms/currentChild';
import { AppGoBackButton } from 'components/atoms';
import { Header } from 'components/templates/child/ChildDetails/components/Header/Header';
import {
    DobleRow,
    GridItem,
    ParentGrid,
} from 'components/templates/child/ChildDetails/InfoContainerBase.styled';
import {
    AdditionalInformation,
    Agreements,
    PersonalData,
    Settlements,
} from 'components/templates/child/ChildDetails/components';
import { ChildSettlementDetails } from 'types';
import { useEffect, useMemo } from 'react';
import { getFullName } from 'utils/getFullName';
import { AddressData, ErrorDisplay, LoadingDisplay } from 'components/molecules';
import { ParentGuardians } from 'components/templates/child/ChildDetails/components/Guardians/ParentGuardians/ParentGuardians';
import { ParentDiscounts } from 'components/templates/child/ChildDetails/components/Discounts/ParentDiscounts/ParentDiscounts';
import parentHTTPService from 'services/HTTPService/parent/ParentHTTPService';
import { useChildDetailsPageNetworkManage } from 'pages/app/parent/child/hooks/useChildDetailsPageNetworkManage';
import { childrenTexts } from 'consts/text';
import { ChildAddressDataIcon } from 'assets';
import { useAppQuery } from 'services/reactQuery/useAppQuery';

export const ParentChildDetailsPage = () => {
    const { id: childId } = useCurrentChild();
    const childEditPagePath = generatePath(appPaths.app.parent['child-profile'].edit, {
        childId,
    });
    const [_, setSearchParams] = useSearchParams();
    const pageRoute = appRoutes.app.parent['child-profile'].base;

    const {
        isChildDetailsFetching,
        isChildDetailsError,
        childDetailsData,
        isSettlementChildDetailsFetching,
        isSettlementChildDetailsError,
        settlementChildDetails,
        isDietDetailsFetching,
        dietDetails,
    } = useChildDetailsPageNetworkManage();

    const { data: parentProfileData, isFetching: isParentProfileFetching } = useAppQuery(
        'PARENT_PROFILE',
        [childId],
        () => parentHTTPService.children.parentProfile.getParentProfile(childId),
        {
            enabled: !!childId,
            refetchOnWindowFocus: false,
        },
    );

    useEffect(() => {
        if (!childDetailsData) {
            return;
        }
        const params = new URLSearchParams(window.location.search);
        params.set(pageRoute, getFullName(childDetailsData.firstName, childDetailsData.lastName));
        setSearchParams(params, { replace: true });
    }, [childDetailsData, pageRoute, setSearchParams]);

    const mappedParentsForSettlement = useMemo<
        { id: string; firstName: string; lastName: string }[]
    >(() => {
        const list: { id: string; firstName: string; lastName: string }[] = [];

        if (childDetailsData) {
            list.push({
                id: childId,
                firstName: 'Dziecko:',
                lastName: `${childDetailsData.firstName} ${childDetailsData.lastName}`,
            });
        }

        if (parentProfileData) {
            list.push({
                id: parentProfileData.id,
                firstName: parentProfileData.firstName,
                lastName: parentProfileData.lastName,
            });
        }

        return list;
    }, [childDetailsData, parentProfileData, childId]);

    if (
        isChildDetailsFetching ||
        isSettlementChildDetailsFetching ||
        isDietDetailsFetching ||
        isParentProfileFetching
    ) {
        return <LoadingDisplay />;
    }

    if (!childDetailsData || isSettlementChildDetailsError || isChildDetailsError) {
        return <ErrorDisplay />;
    }

    const childDetailsWithDietDetails = { ...childDetailsData, dietDetails };

    return (
        <div>
            <AppGoBackButton />
            <Header
                {...childDetailsData}
                childEditPagePath={childEditPagePath}
                canManageChildAvatar
                httpMethod={{
                    ...parentHTTPService.files,
                    ...parentHTTPService.children.profilePhoto,
                }}
            />
            <ParentGrid>
                <GridItem>
                    <PersonalData {...childDetailsData} canSeeSensitiveData />
                </GridItem>
                <GridItem>
                    <AddressData
                        heading={childrenTexts.childDetails.addressData.addressData}
                        icon={<ChildAddressDataIcon />}
                        {...childDetailsData}
                    />
                </GridItem>
                <GridItem width={7}>
                    <AdditionalInformation {...childDetailsWithDietDetails} />
                </GridItem>
                <GridItem width={7}>
                    <Settlements
                        {...(settlementChildDetails as ChildSettlementDetails)}
                        parents={mappedParentsForSettlement}
                        childFullName={`${childDetailsData.firstName} ${childDetailsData.lastName}`}
                    />
                </GridItem>
                <DobleRow>
                    <Agreements {...childDetailsData} />
                </DobleRow>
                <GridItem width={9}>
                    <ParentDiscounts />
                </GridItem>
                <GridItem width={6}>
                    <ParentGuardians {...childDetailsData} />
                </GridItem>
            </ParentGrid>
        </div>
    );
};
